/**
 * What a delete will do, as sentences.
 *
 * The API answers with the plan (`DeleteImpact`): what is removed, what is only
 * unlinked, what is left as written. This turns that into the few things a
 * person needs to read before confirming, and nothing else - so the dialog is
 * layout and this is wording, and the wording is testable without a browser.
 *
 * The rule the sentences state is the issue's (#80): deleting an element removes
 * it, what is written inside it, and the links to and from it. It does not
 * delete another element because that element was linked to this one. A reader
 * who sees "will lose X as a source" must be able to trust that the requirement
 * is still there afterwards.
 */

import type { DeleteEffect, DeleteImpact } from '../../schema'

/** How many names to spell out before saying "and N more". */
export const NAME_LIMIT = 8

/** A kind as it reads in a sentence, singular. */
const KIND_WORD: Record<string, string> = {
  design_part: 'part',
  design_port: 'port',
  connection: 'connection',
  transition: 'transition',
  annotation: '@InState annotation',
  state: 'state',
  function: 'function',
  verification: 'verification',
  interface: 'interface',
  concern: 'concern',
  view: 'view',
  requirement: 'requirement',
}

export function kindWord(kind: string): string {
  return KIND_WORD[kind] ?? kind.replace(/_/g, ' ')
}

/** "a, b and c", spelling out at most `limit` of them. */
export function nameList(names: string[], limit = NAME_LIMIT): string {
  const shown = names.slice(0, limit)
  const more = names.length - shown.length
  if (more > 0) return `${shown.join(', ')} and ${more} more`
  if (shown.length > 1) return `${shown.slice(0, -1).join(', ')} and ${shown.at(-1)}`
  return shown.join('')
}

export function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

export interface DeleteSummary {
  /** Always one line: the thing being deleted. */
  headline: string
  /** Removed along with it, one sentence per group. Empty for most requirements. */
  alongside: string[]
  /** Requirements that lose it as a source, and who is left with none. Empty if none. */
  sources: string[]
  /** Every other link that is removed or cleared; the other element stays. */
  unlinks: DeleteEffect[]
  /** References the other element needs, left exactly as written. */
  kept: DeleteEffect[]
  /** Removed because it cannot exist without something that is going. */
  dependents: Array<{ kind: string; identifier: string; because: string }>
  /** How many elements go, counting the one deleted. */
  total: number
  /** True when the only thing that goes is the element itself. */
  alone: boolean
}

export function summarize(impact: DeleteImpact): DeleteSummary {
  const label = impact.identifier
  const itself = impact.removes.filter((entry) => entry.reason === 'itself')
  const inside = impact.removes.filter((entry) => entry.reason === 'inside')
  const instances = impact.removes.filter((entry) => entry.reason === 'instance')
  const depends = impact.removes.filter((entry) => entry.reason === 'depends')

  const alongside: string[] = []
  if (inside.length) {
    alongside.push(
      `Written inside it, so it goes too: ${groupByKind(inside.map((e) => [e.kind, e.identifier]))}.`,
    )
  }
  if (instances.length) {
    alongside.push(
      `${plural(instances.length, 'usage')} of ${label}, which cannot outlive the definition ` +
        `they are instances of: ${nameList(instances.map((entry) => entry.identifier))}.`,
    )
  }
  // The half of the issue that is easiest to fear wrongly: a part delete takes
  // usages, never the definitions they are usages OF.
  if (impact.kind === 'design_part' && (inside.length || instances.length)) {
    alongside.push('Only usages go. The definitions those usages are instances of stay in the model.')
  }

  const lost = impact.unlinks.filter((entry) => entry.relation === 'source')
  const sources: string[] = []
  if (lost.length) {
    sources.push(
      `${plural(lost.length, 'requirement')} will lose ${label} as a source and stay: ` +
        `${nameList(lost.map((entry) => entry.identifier))}.`,
    )
    sources.push(
      impact.orphaned.length
        ? `${plural(impact.orphaned.length, 'requirement')} will be left with no source at all, ` +
            `which BEST reports as an orphan finding and not an error: ${nameList(impact.orphaned)}.`
        : 'None of them is left without a source.',
    )
  }

  return {
    headline: `${label} will be removed from the model.`,
    alongside,
    sources,
    unlinks: impact.unlinks.filter((entry) => entry.relation !== 'source'),
    kept: impact.kept,
    dependents: depends.map((entry) => ({
      kind: entry.kind,
      identifier: entry.identifier,
      because: entry.because ?? '',
    })),
    total: impact.removes.length,
    alone: impact.removes.length === 1 && itself.length === 1,
  }
}

/** "parts: a, b; ports: c" - grouped, because a part owns ports and usages. */
function groupByKind(entries: Array<[string, string]>): string {
  const groups = new Map<string, string[]>()
  for (const [kind, name] of entries) {
    groups.set(kind, [...(groups.get(kind) ?? []), name])
  }
  return [...groups.entries()]
    .map(([kind, names]) => {
      const word = kindWord(kind)
      return `${names.length === 1 ? word : `${word}s`} ${nameList(names)}`
    })
    .join('; ')
}

/** The heading of the section that tells someone more than they clicked on is going. */
export function dependentsHeading(identifier: string): string {
  return `Also removed, because it cannot exist without ${identifier}`
}

/** The confirm button: the bare word for one element, a count for more. */
export function confirmLabel(total: number): string {
  return total > 1 ? `Delete ${total} elements` : 'Delete'
}
